import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import CountdownTimer from "@/components/CountdownTimer";
import { useAuth } from "@/context/AuthContext";
import { Coins, TrendingUp, Users, Trophy, ArrowLeft, Zap } from "lucide-react";

export default function AuctionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, updateTokens } = useAuth();
  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [flashKey, setFlashKey] = useState(0);
  const priceRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/auctions/${id}`);
      setAuction(data.auction);
      setBids(data.recent_bids);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [load]);

  const placeBid = async () => {
    if (!user || typeof user !== "object") {
      toast.error("Login to place a bid");
      navigate("/login");
      return;
    }
    if ((user.tokens || 0) < 1) {
      toast.error("You're out of tokens. Grab a pack!");
      navigate("/wallet");
      return;
    }
    setPlacing(true);
    try {
      const { data } = await api.post("/auctions/bid", { auction_id: id });
      setAuction(data.auction);
      updateTokens(data.tokens);
      setFlashKey((k) => k + 1);
      toast.success(`Bid placed at $${data.auction.current_price.toFixed(2)}`);
      if (data.auction.status === "closed") {
        toast.success("Auction ended!", { duration: 5000 });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bid failed");
    } finally {
      setPlacing(false);
    }
  };

  if (loading && !auction) {
    return <div className="max-w-7xl mx-auto px-6 py-16 text-zinc-500">Loading…</div>;
  }
  if (!auction) {
    return <div className="max-w-7xl mx-auto px-6 py-16 text-zinc-500">Auction not found.</div>;
  }

  const isClosed = auction.status !== "live";
  const canBid = user && typeof user === "object" && !isClosed && !placing;

  return (
    <div className="grid-bg min-h-screen">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Link
          to="/"
          data-testid="back-link"
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft size={16} /> All auctions
        </Link>

        <div className="grid md:grid-cols-12 gap-8">
          {/* Image */}
          <div className="md:col-span-7">
            <div className="card-surface overflow-hidden p-0">
              <div className="aspect-[4/3] bg-black">
                <img
                  src={auction.image_url}
                  alt={auction.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Recent bids */}
            <div className="card-surface p-6 mt-6">
              <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
                <TrendingUp size={16} /> Recent bids
              </h3>
              {bids.length === 0 ? (
                <p className="text-zinc-500 text-sm">No bids yet. Be first!</p>
              ) : (
                <ul className="divide-y divide-white/5">
                  {bids.map((b) => (
                    <li
                      key={b.id}
                      className="flex items-center justify-between py-2.5 text-sm"
                    >
                      <span className="font-medium">{b.user_name}</span>
                      <span className="font-mono-nums text-[#FBBF24] font-bold">
                        ${b.price_at_bid.toFixed(2)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Bid Panel */}
          <div className="md:col-span-5">
            <span className={`chip ${isClosed ? "chip-closed" : "chip-live"} mb-4`}>
              {isClosed ? "Auction Closed" : "● Live Auction"}
            </span>
            <h1 className="font-display font-extrabold text-4xl tracking-tighter mb-2">
              {auction.title}
            </h1>
            <p className="text-zinc-400 text-sm mb-8 leading-relaxed">
              {auction.description}
            </p>

            <div key={flashKey} className={`card-surface p-6 mb-6 ${flashKey ? "bid-flash" : ""}`}>
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                    Current bid
                  </div>
                  <div
                    ref={priceRef}
                    data-testid="current-price"
                    className="font-mono-nums font-bold text-5xl text-[#FBBF24]"
                  >
                    ${auction.current_price.toFixed(2)}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    Retail{" "}
                    <span className="line-through">${auction.retail_price.toFixed(2)}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                    Time left
                  </div>
                  <CountdownTimer
                    endsAt={auction.ends_at}
                    status={auction.status}
                    size="lg"
                    onExpire={load}
                  />
                </div>
              </div>

              {/* Profit progress — THE close trigger */}
              <div className="mb-6">
                <div className="flex items-center justify-between text-xs mb-2">
                  <span className="text-zinc-400 uppercase tracking-widest">
                    Closes at $500 profit
                  </span>
                  <span
                    className="font-mono-nums text-[#FBBF24] font-bold"
                    data-testid="profit-progress-percent"
                  >
                    {auction.progress_percent}%
                  </span>
                </div>
                <div className="progress-track">
                  <div
                    className="progress-fill"
                    style={{ width: `${auction.progress_percent}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-[11px] text-zinc-500 mt-2 font-mono-nums">
                  <span>Profit: ${auction.profit?.toFixed(2) ?? "0.00"}</span>
                  <span>Target: $500.00</span>
                </div>
              </div>

              {isClosed ? (
                <div className="rounded-xl border border-white/10 p-5 text-center">
                  <Trophy className="mx-auto text-[#FBBF24] mb-2" size={28} />
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Winner</div>
                  <div className="font-display font-bold text-xl">
                    {auction.winner_name || "No winner"}
                  </div>
                  <div className="font-mono-nums text-sm text-zinc-400 mt-2">
                    Sold at ${auction.final_price?.toFixed(2)}
                  </div>
                </div>
              ) : (
                <button
                  onClick={placeBid}
                  disabled={!canBid}
                  data-testid="place-bid-button"
                  className="btn-bid w-full flex items-center justify-center gap-3 text-base"
                >
                  <Zap size={18} strokeWidth={3} />
                  {placing ? "Placing…" : "Bid Now — 1 token ($0.50)"}
                </button>
              )}

              <div className="grid grid-cols-2 gap-3 mt-6 pt-6 border-t border-white/5 text-sm">
                <Stat icon={<Users size={14} />} label="Total bids" value={auction.bid_count} />
                <Stat
                  icon={<Coins size={14} />}
                  label="Your tokens"
                  value={
                    user && typeof user === "object" ? (
                      <span className="text-[#FBBF24]">{user.tokens}</span>
                    ) : (
                      "—"
                    )
                  }
                />
              </div>
            </div>

            {!user && (
              <Link to="/login" className="btn-outline w-full text-center block">
                Login to bid
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
        {icon} {label}
      </div>
      <div className="font-mono-nums font-bold text-lg">{value}</div>
    </div>
  );
}
